import {darken, styled} from '@mui/material/styles';
import {DefaultFlatpickrStyles} from './DefaultFlatpickrStyles';
import {excludeComponentProps} from '../utils/excludeComponentProps';

interface DatePickerContainerProps {
    inline?: boolean;
    isDense?: boolean;
    hideYear?: boolean;
    showMonths?: number;
}

export const DatePickerContainer = styled(DefaultFlatpickrStyles, {
    ...excludeComponentProps('inline', 'isDense', 'hideYear', 'showMonths')
})<DatePickerContainerProps>(({
    theme, hideYear, showMonths = 1, isDense
}) => {
    // controls
    const multiMonthSpacing = '1px';
    const boxHeight = isDense ? '36px' : '40px';
    const calendarFontSize = isDense ? theme.tokens.FuelFontSizeSm : theme.tokens.FuelFontSizeBody;
    const controlFontSize = isDense ? theme.tokens.FuelFontSizeBody : theme.tokens.FuelFontSizeLg;
    const calendarWidth = isDense ? '258px' : '286px';
    const containerPadding = '3px';
    const calendarPadding = isDense ? '0.5rem' : '1rem';
    const iconButtonSize = isDense ? '28px' : '32px';
    const rangeInputSpacing = '1rem';
    const timePickerHeight = isDense ? '36px' : '40px';
    const yearControlWidth = isDense ? '4rem' : '4.5rem';

    return {

        // START: Input Styling
        '& .MuiInputBase-root.MuiInputBase-adornedStart': {
            '& .flatpickr-calendar.static.open': {
                marginLeft: '-2.25rem'
            }
        },

        '& .flatpickr-input': {
            '&.rangePicker': {
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gridGap: rangeInputSpacing
            }
        },
        // End: Input Styling

        '& .flatpickr-wrapper': {
            width: '100%'
        },

        '& .flatpickr-calendar': {
            WebkitBoxShadow: theme.tokens.FuelShadowMd,
            boxShadow: theme.tokens.FuelShadowMd,
            borderRadius: '0.5rem',
            backgroundColor: theme.tokens.FuelColorWhite,
            paddingTop: calendarPadding,
            paddingBottom: calendarPadding,
            color: theme.tokens.FuelColorTextPrimary,
            width: calendarWidth,
            overflow: 'hidden',

            // CALENDAR POSITIONING in respect to input
            marginTop: theme.tokens.FuelSpacing1,

            // 16px spacing
            '& .flatpickr-months': {
                width: '100%',
                height: 'auto',

                '& .flatpickr-prev-month, .flatpickr-next-month': {
                    top: 'auto',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',

                    borderRadius: theme.tokens.FuelBorderRadiusBase,
                    height: iconButtonSize,
                    width: iconButtonSize,
                    backgroundColor: theme.tokens.FuelColorBackgroundActionDefaultBase,

                    '&:before': {
                        position: 'relative',
                        content: '""',
                        display: 'inline-block',
                        width: '0.5rem', // estimate
                        height: '0.5rem', // estimate
                        borderRight: '0.2em solid transparent',
                        borderTop: '0.2em solid transparent',
                        borderColor: theme.tokens.FuelColorGray700
                    },

                    '&:hover': {
                        backgroundColor: theme.tokens.FuelColorBackgroundActionDefaultHovered,
                        '&:before': {
                            borderColor: theme.tokens.FuelColorTextLinkHovered
                        }
                    },

                    svg: {
                        display: 'none'
                    },

                    '&.flatpickr-disabled': {
                        opacity: 0.75,
                        cursor: 'disabled'
                    }
                },

                '& .flatpickr-prev-month:before': {
                    transform: 'rotate(-135deg)',
                    marginLeft: '4px'
                },

                '& .flatpickr-next-month:before': {
                    transform: 'rotate(45deg)',
                    marginRight: '4px'
                },

                '& .flatpickr-month': {
                    display: 'flex',
                    alignItems: 'center',

                    paddingLeft: containerPadding,
                    paddingRight: containerPadding,

                    '& .flatpickr-current-month': {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',

                        // flexWrap: 'wrap',

                        flexGrow: 1,
                        paddingRight: theme.tokens.FuelSpacing1,
                        paddingLeft: theme.tokens.FuelSpacing1,

                        fontSize: controlFontSize,
                        fontWeight: theme.tokens.FuelFontWeightRegular,

                        '& .flatpickr-monthDropdown-months': { // <select>
                            fontWeight: theme.tokens.FuelFontWeightRegular,
                            fontSize: controlFontSize,

                            paddingLeft: theme.tokens.FuelSpacing1,
                            paddingRight: theme.tokens.FuelSpacing3,
                            borderRadius: theme.tokens.FuelBorderRadiusMd,
                            width: 'min-content',

                            '&:hover': {
                                background: theme.tokens.FuelColorBackgroundActionDefaultHovered
                            },

                            '& .flatpickr-monthDropdown-month': {
                                fontSize: controlFontSize,
                                textAlign: hideYear ? 'center' : 'left'
                            }
                        },

                        '& .numInputWrapper': {
                            display: hideYear ? 'none' : 'inline-block',
                            width: yearControlWidth,

                            fontWeight: theme.tokens.FuelFontWeightRegular,
                            fontSize: controlFontSize,

                            paddingLeft: theme.tokens.FuelSpacing1,
                            paddingRight: theme.tokens.FuelSpacing4,
                            borderRadius: theme.tokens.FuelBorderRadiusMd,
                            marginLeft: theme.tokens.FuelSpacing2,

                            '&:hover': {
                                background: theme.tokens.FuelColorBackgroundActionDefaultHovered
                            },

                            '& .numInput': {
                                '&[disabled]': {
                                    display: 'none'
                                }
                            }
                        }
                    }
                }
            },

            '& .flatpickr-innerContainer': {
                width: '100%',
                marginTop: calendarPadding,

                '& .flatpickr-rContainer': {
                    width: '100%',

                    '& .flatpickr-weekdays': {
                        height: 'auto',

                        '& .flatpickr-weekdaycontainer': {
                            paddingLeft: containerPadding,
                            paddingRight: containerPadding,

                            '& .flatpickr-weekday': {
                                height: boxHeight,
                                width: boxHeight,
                                color: theme.tokens.FuelColorTextTertiary,
                                fontSize: theme.tokens.FuelFontSizeXs,
                                fontWeight: theme.tokens.FuelFontWeightSemibold,

                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }
                        }
                    },

                    '& .flatpickr-days': {
                        width: '100% !important',

                        '& .dayContainer': {
                            width: '100%',
                            flexWrap: 'wrap',
                            paddingLeft: containerPadding,
                            paddingRight: containerPadding,

                            '& .flatpickr-day': {
                                position: 'relative',
                                fontSize: calendarFontSize,
                                height: boxHeight,
                                width: boxHeight,
                                flex: 'none',

                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',

                                '&:hover': {
                                    backgroundColor: theme.tokens.FuelColorGray200
                                },

                                '&.selected, &.startRange, &.endRange': {
                                    borderColor: 'transparent',
                                    color: theme.tokens.FuelColorWhite,
                                    backgroundColor: theme.tokens.FuelColorBrandPrimary,

                                    '&:hover': {
                                        backgroundColor: darken(theme.tokens.FuelColorBrandPrimary, 0.25)
                                    }
                                },

                                '&.inRange': {
                                    backgroundColor: theme.name === 'jdGreen' ? theme.tokens.FuelColorGreen200 : theme.tokens.FuelColorGray200,
                                    color: theme.tokens.FuelColorTextPrimary,
                                    borderColor: 'transparent'
                                },

                                '&.today': {
                                    '&:not(.startRange):not(.endRange):not(.inRange)': {
                                        borderColor: darken(theme.tokens.FuelColorBrandPrimary, 0.95),
                                        color: theme.tokens.FuelColorTextPrimary,

                                        '&.selected': {
                                            color: theme.tokens.FuelColorWhite
                                        }
                                    }
                                },

                                '&.flatpickr-disabled': {
                                    color: theme.tokens.FuelColorTextDisabled,
                                    background: 'transparent',
                                    borderColor: 'transparent',
                                    cursor: 'not-allowed',

                                    '&:hover': {
                                        cursor: 'not-allowed',
                                        color: theme.tokens.FuelColorTextDisabled
                                    }
                                }
                            }
                        }
                    }
                }
            },

            '& .flatpickr-time': {
                height: timePickerHeight,
                marginTop: calendarPadding,
                borderTop: `solid 1px ${ theme.tokens.FuelColorBorderLight}`
            },

            '&.hasTime': {
                paddingBottom: 0,

                '&.noCalendar': {
                    paddingTop: 0,

                    '& .flatpickr-time': {
                        marginTop: 0,
                        borderTop: 'none'
                    }
                }
            },

            // ** MULTI MONTH **
            '&.multiMonth': {
                width: `calc(${calendarWidth} * ${showMonths} + ${multiMonthSpacing} * ${showMonths - 1}) !important`,

                '&:after': {
                    content: '""',
                    display: 'block',
                    width: multiMonthSpacing,
                    height: '100%',
                    position: 'absolute',
                    top: 0,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    backgroundColor: theme.tokens.FuelColorBorderLight
                },

                '& .flatpickr-months': {
                    '& .flatpickr-month + .flatpickr-month': {
                        marginLeft: multiMonthSpacing
                    }
                },

                '& .flatpickr-innerContainer .flatpickr-rContainer': {
                    '& .flatpickr-weekdaycontainer + .flatpickr-weekdaycontainer': {
                        marginLeft: multiMonthSpacing
                    },
                    '& .flatpickr-days .dayContainer + .dayContainer': {
                        marginLeft: multiMonthSpacing
                    }
                }

            },

            // spacing
            '& .form-control': {
                color: theme.tokens.FuelColorGray900,
                fontSize: theme.tokens.FuelFontSizeMd,
                lineHeight: '1.25rem',
                maxWidth: 'auto',
                minWidth: '12.5rem'
            },

            '& .numInputWrapper': {
                '& .numInput': {
                    '&:hover': {
                        background: theme.tokens.FuelColorBackgroundActionDefaultHovered
                    }
                },

                '& .arrowUp, & .arrowDown': {
                    position: 'absolute',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',

                    '&:before': {
                        content: '""',
                        width: '0px',
                        height: '0px',
                        position: 'absolute'
                    }
                },

                '& .arrowUp': {
                    top: 0,

                    '&:before': {
                        transform: 'translateY(-1.75px)',
                        borderTop: '4px solid transparent',
                        borderBottom: `4px solid ${ theme.tokens.FuelColorTextSecondary}`,
                        borderRight: '4px solid transparent',
                        borderLeft: '4px solid transparent'
                    }
                },

                '& .arrowDown': {
                    top: '50%',

                    '&:before': {
                        transform: 'translateY(2.25px)',
                        borderTop: `4px solid ${ theme.tokens.FuelColorTextSecondary}`,
                        borderRight: '4px solid transparent',
                        borderBottom: '4px solid transparent',
                        borderLeft: '4px solid transparent'
                    }
                }
            }
        }
    };
});
